import { FileText, HelpCircle, Search } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { searchKb } from "@/features/knowledge/api";
import type { SearchResults } from "@/features/knowledge/types";

export default function KbHomePage() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState<SearchResults | null>(null);
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const term = q.trim();
    if (!term) {
      setResults(null);
      return;
    }
    setLoading(true);
    try {
      setResults(await searchKb(term));
    } finally {
      setLoading(false);
    }
  }

  const empty = results && results.faq.length === 0 && results.wiki.length === 0;

  return (
    <div className="flex flex-col gap-6">
      <form onSubmit={handleSubmit} className="relative max-w-xl">
        <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search FAQs and wiki articles…"
          className="pl-10"
          aria-label="Search the knowledge base"
        />
      </form>

      {loading && <p className="text-muted-foreground text-sm">Searching…</p>}

      {empty && <p className="text-muted-foreground text-sm">No results for “{q}”.</p>}

      {results && results.faq.length > 0 && (
        <section>
          <h2 className="mb-2 text-sm font-semibold tracking-wide uppercase">FAQ</h2>
          <div className="flex flex-col gap-2">
            {results.faq.map((hit) => (
              <Card key={hit.id} className="p-4">
                <Link to="/kb/faq" className="flex items-start gap-3">
                  <HelpCircle className="text-muted-foreground mt-0.5 size-4 shrink-0" />
                  <div>
                    <p className="font-medium">{hit.title}</p>
                    <p className="text-muted-foreground text-sm">{hit.snippet}</p>
                  </div>
                </Link>
              </Card>
            ))}
          </div>
        </section>
      )}

      {results && results.wiki.length > 0 && (
        <section>
          <h2 className="mb-2 text-sm font-semibold tracking-wide uppercase">Wiki</h2>
          <div className="flex flex-col gap-2">
            {results.wiki.map((hit) => (
              <Card key={hit.id} className="p-4">
                <Link to={`/kb/wiki/${hit.slug}`} className="flex items-start gap-3">
                  <FileText className="text-muted-foreground mt-0.5 size-4 shrink-0" />
                  <div>
                    <p className="font-medium">{hit.title}</p>
                    <p className="text-muted-foreground text-sm">{hit.snippet}</p>
                  </div>
                </Link>
              </Card>
            ))}
          </div>
        </section>
      )}

      {!results && !loading && (
        <p className="text-muted-foreground text-sm">
          Search across frequently-asked questions and wiki articles, or browse the tabs above.
        </p>
      )}
    </div>
  );
}
